import TopSymbol from "features/watchlist/TopSymbols/TopSymbol";
import { AnimatePresence, motion } from "motion/react";
import type { Variants } from "motion/react";
import React, { useMemo } from "react";
import { useTheme } from "styled-components";
import { watchlistService } from "app/services/watchlist.service";
import { getTextWidth } from "common/Text/utils.ts";
import {
  NEW_LOOK_TICKER_WIDTH,
  SYMBOL_FONT_SIZE,
  SYMBOL_FONT_WEIGHT,
  SYMBOL_HORIZONTAL_MARGIN,
  SYMBOL_ICON_MARGIN,
  SYMBOL_ICON_SIZE,
} from "features/watchlist/TopSymbols/constants.ts";

interface TopSymbolsListProps {
  maxWidth: number;
  /** Shows up to `maxSymbols` chips in a horizontally scrolling row. */
  scrollable?: boolean;
  maxSymbols?: number;
}

const TopSymbolsList = ({
  maxWidth,
  scrollable,
  maxSymbols = MAX_NUM_OF_SYMBOLS,
}: TopSymbolsListProps) => {
  const { isNewLook } = useTheme();
  const { data: topSymbolsList } = watchlistService.useGetWatchlistQuery();

  const longestSymbol = useMemo(() => {
    return topSymbolsList?.sortedWatchlist
      .slice(0, maxSymbols)
      .sort((a, b) => b.symbol.length - a.symbol.length)[0]?.symbol;
  }, [topSymbolsList]);

  const symbolMaxWidth = useMemo(() => {
    const textWidth = getTextWidth(
      longestSymbol,
      SYMBOL_FONT_SIZE,
      SYMBOL_FONT_WEIGHT
    );
    if (isNewLook)
      return (
        Math.max(textWidth, NEW_LOOK_MIN_LINE_WIDTH) + NEW_LOOK_CHIP_CHROME
      );
    return (
      textWidth +
      SYMBOL_ICON_SIZE +
      SYMBOL_ICON_MARGIN +
      2 * SYMBOL_HORIZONTAL_MARGIN
    );
  }, [longestSymbol, isNewLook]);

  const symbolCount = useMemo(() => {
    if (scrollable) return maxSymbols;
    if (isNewLook)
      return Math.min(Math.floor(maxWidth / NEW_LOOK_TICKER_WIDTH), maxSymbols);
    return Math.min(Math.floor(maxWidth / symbolMaxWidth), maxSymbols);
  }, [maxWidth, symbolMaxWidth, scrollable, maxSymbols, isNewLook]);

  return (
    <div style={scrollable ? SCROLLABLE_STYLE : ROW_STYLE}>
      <AnimatePresence>
        {topSymbolsList?.sortedWatchlist
          .slice(0, symbolCount)
          .map((market, i) => (
            <motion.div
              key={`${market.exchange}_${market.symbol}`}
              custom={i}
              layout={!isNewLook}
              style={{ flex: "none" }}
              initial="hidden"
              animate="visible"
              variants={variants}
            >
              <TopSymbol key={i} market={market} />
            </motion.div>
          ))}
      </AnimatePresence>
    </div>
  );
};

export default React.memo(TopSymbolsList);

const MAX_NUM_OF_SYMBOLS = 5;
const ROW_STYLE = { display: "flex" };
const SCROLLABLE_STYLE = {
  display: "flex",
  overflowX: "auto",
  scrollbarWidth: "none",
} as const;
// A new-look chip: padding and icon, then the wider of the symbol line and the
// price-and-change line.
const NEW_LOOK_MIN_LINE_WIDTH = 80;
const NEW_LOOK_CHIP_CHROME = 36;
const variants: Variants = {
  visible: {
    opacity: 1,
    transition: { ease: "easeIn", duration: 0.3 },
  },
  hidden: { opacity: 0 },
};
